import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { usePlans } from '@/presentation/hooks/usePlans';
import { usePanelPagos } from '@/presentation/hooks/usePanelPagos';
import { useFinancialReport } from '@/presentation/hooks/useFinancialReport';
import { useClinicReport } from '@/presentation/hooks/useClinicReport';
import { useSaasMetrics } from '@/presentation/hooks/useSaasMetrics';
import { httpClient } from '@/infrastructure/http';
import { formatNovedadPrice, novedadCopyKey, usePanelNovedades } from '@/presentation/hooks/usePanelNovedades';

function isPagoAbonado(estadoDescripcion: string): boolean {
  return (estadoDescripcion ?? '').trim().toLowerCase() === 'abonado';
}

const StatIcon = ({
  children,
  className = 'text-teal-600 dark:text-teal-400',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-gray-700/80 md:flex lg:h-10 lg:w-10 ${className}`}
  >
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      {children}
    </svg>
  </div>
);

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { plans, isLoading: plansLoading } = usePlans();
  const { pagos, isLoading: pagosLoading } = usePanelPagos();
  const { report: financial, isLoading: financialLoading } = useFinancialReport();
  const { report: clinics, isLoading: clinicsLoading } = useClinicReport();
  const { metrics: saas, isLoading: saasLoading } = useSaasMetrics();

  const [pendingSolicitudes, setPendingSolicitudes] = useState<number | null>(null);
  const { novedades, error: novedadesError } = usePanelNovedades(20, { poll: false });

  useEffect(() => {
    httpClient
      .get<{ id: number }[]>('/solicitudes-tenant?estado=pendiente')
      .then((res) => setPendingSolicitudes(res.data.length))
      .catch(() => setPendingSolicitudes(null));
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const activePlans = plans.filter((p) => p.isActive()).length;

    const monthRevenue =
      financial?.monthlyRevenue.find((item) => item.month === currentPeriod)?.revenue ?? 0;

    const recentPayments = pagos.filter(
      (p) =>
        p.fechaPeriodoPago === currentPeriod &&
        p.monto > 0 &&
        isPagoAbonado(p.estadoPagoDescripcion)
    ).length;

    return {
      totalTenants: clinics?.totalClinics ?? 0,
      activeTenants: clinics?.activeClinics ?? 0,
      inactiveTenants: clinics?.inactiveClinics ?? 0,
      totalRevenue: financial?.totalRevenue ?? 0,
      pendingRevenue: (financial?.pendingRevenue ?? 0) + (financial?.overdueRevenue ?? 0),
      monthRevenue,
      currentPeriod,
      activePlans,
      totalPlans: plans.length,
      recentPayments,
    };
  }, [plans, pagos, financial, clinics]);

  const isLoading = plansLoading || pagosLoading || financialLoading || clinicsLoading || saasLoading;

  const activeTenantPercentage =
    stats.totalTenants > 0 ? (stats.activeTenants / stats.totalTenants) * 100 : 0;
  const inactiveTenantPercentage =
    stats.totalTenants > 0 ? (stats.inactiveTenants / stats.totalTenants) * 100 : 0;

  if (isLoading && !financial && !clinics) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-page panel-safe-bottom">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.dashboard.title')}</h1>
          <p className="panel-page-subtitle">
            {t('pages.dashboard.welcome')}, {user?.username}!
          </p>
        </div>
      </div>

      {pendingSolicitudes !== null && pendingSolicitudes > 0 && (
        <button
          onClick={() => navigate('/solicitudes')}
          className="w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/80 dark:border-amber-600 text-left hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
        >
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-lg dark:bg-amber-900/50">
            &#9873;
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {t('pages.dashboard.pendingSolicitudesAlert.title', { count: pendingSolicitudes })}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t('pages.dashboard.pendingSolicitudesAlert.subtitle')}
            </p>
          </div>
          <span className="text-amber-500 dark:text-amber-400 text-lg">&rsaquo;</span>
        </button>
      )}

      <div className="panel-card p-3.5 md:p-4 lg:p-5">
        <h2 className="text-base font-bold text-slate-900 dark:text-white md:text-lg">
          {t('pages.dashboard.novedades.title')}
        </h2>
        <p className="mt-0.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.dashboard.novedades.subtitle')}
        </p>
        {novedadesError ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.dashboard.novedades.error')}</p>
        ) : novedades.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.dashboard.novedades.empty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-gray-700">
            {novedades.map((item) => {
              const price = formatNovedadPrice(item.precioUnitario);
              const copyKey = novedadCopyKey(item.tipo);
              const when = item.fechaCreacion
                ? new Date(item.fechaCreacion).toLocaleString()
                : '';
              return (
                <li key={item.id} className="py-2.5 first:pt-0 last:pb-0">
                  <p className="text-sm text-slate-800 dark:text-slate-100">
                    {t(copyKey, { clinic: item.nombreClinica, price })}
                  </p>
                  {when ? (
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{when}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 lg:gap-4 xl:gap-6">
        <div className="panel-stat border-l-4 border-teal-500">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="panel-stat-label">{t('pages.dashboard.totalTenants')}</p>
              <p className="panel-stat-value">{stats.totalTenants}</p>
              <p className="mt-1 text-[11px] text-green-600 dark:text-green-400 md:text-xs">
                {stats.activeTenants} {t('pages.dashboard.active')}
              </p>
            </div>
            <StatIcon>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </StatIcon>
          </div>
        </div>

        <div className="panel-stat border-l-4 border-green-500">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="panel-stat-label">{t('pages.dashboard.totalRevenue')}</p>
              <p className="panel-stat-value text-lg md:text-2xl">
                ${stats.totalRevenue.toLocaleString()}
              </p>
              <p className="mt-1 truncate text-[11px] text-yellow-600 dark:text-yellow-400 md:text-xs">
                ${stats.pendingRevenue.toLocaleString()} {t('pages.dashboard.pending')}
              </p>
              <p className="mt-1 hidden text-xs text-gray-500 dark:text-gray-400 md:block">
                {t('pages.dashboard.thisMonth')}: ${stats.monthRevenue.toLocaleString()}
              </p>
            </div>
            <StatIcon className="text-green-600 dark:text-green-400">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </StatIcon>
          </div>
        </div>

        <div className="panel-stat border-l-4 border-teal-500">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="panel-stat-label">{t('pages.dashboard.activePlans')}</p>
              <p className="panel-stat-value">{stats.activePlans}</p>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 md:text-xs">
                {stats.totalPlans} {t('pages.dashboard.total')}
              </p>
            </div>
            <StatIcon>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </StatIcon>
          </div>
        </div>

        <div className="panel-stat border-l-4 border-orange-500">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="panel-stat-label">{t('pages.dashboard.recentPayments')}</p>
              <p className="panel-stat-value">{stats.recentPayments}</p>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 md:text-xs">
                {t('pages.dashboard.thisMonth')} ({stats.currentPeriod})
              </p>
            </div>
            <StatIcon className="text-orange-600 dark:text-orange-400">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </StatIcon>
          </div>
        </div>
      </div>

      {saas && (
        <div className="space-y-3 md:space-y-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 className="text-base font-bold text-slate-900 dark:text-white md:text-lg lg:text-xl">
              {t('pages.dashboard.saasTitle')}
            </h2>
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              <button
                type="button"
                onClick={() => navigate('/collections')}
                className="inline-flex h-9 min-h-11 items-center rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700 md:h-10 md:text-sm"
              >
                {t('pages.dashboard.goCollections')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/ecosystem-usage')}
                className="inline-flex h-9 min-h-11 items-center rounded-full border border-teal-200 bg-teal-50 px-3.5 text-xs font-medium text-teal-800 shadow-sm transition hover:bg-teal-100 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-900/50 md:h-10 md:text-sm"
              >
                {t('pages.dashboard.goEcosystemUsage')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 md:gap-3 lg:grid-cols-6">
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.mrr')}</p>
              <p className="panel-stat-value text-lg md:text-xl">${saas.mrr.toLocaleString()}</p>
              <p className="mt-1 text-[11px] text-green-600 dark:text-green-400 md:text-xs">
                {t('pages.dashboard.collectedMrr')}: ${saas.collectedMrr.toLocaleString()}
              </p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.efectividad')}</p>
              <p className="panel-stat-value text-lg md:text-xl">{saas.efectividad.toFixed(1)}%</p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.arpa')}</p>
              <p className="panel-stat-value text-lg md:text-xl">
                ${saas.arpa.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.moraPercent')}</p>
              <p className="panel-stat-value text-lg text-red-600 dark:text-red-400 md:text-xl">
                {saas.moraPercent.toFixed(1)}%
              </p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.churnRate')}</p>
              <p className="panel-stat-value text-lg md:text-xl">{saas.churnRate.toFixed(1)}%</p>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 md:text-xs">
                {saas.suspended} {t('pages.dashboard.suspended')}
              </p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">{t('pages.dashboard.trialsActives')}</p>
              <p className="panel-stat-value text-lg md:text-xl">
                {saas.trials} / {saas.actives}
              </p>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 md:text-xs">
                +{saas.nuevasMes} / -{saas.bajasMes} {t('pages.dashboard.thisMonth')}
              </p>
            </div>
          </div>

          <div className="panel-card p-3.5 md:p-4 lg:p-5">
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              {t('pages.dashboard.monthlySeries')}
            </h3>
            <div className="space-y-2">
              {[...saas.monthlySeries]
                .reverse()
                .slice(0, 6)
                .map((point) => {
                  const max = Math.max(...saas.monthlySeries.map((p) => p.billed), 1);
                  return (
                    <div key={point.month} className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-2 md:grid-cols-[5rem_1fr_auto] md:gap-3">
                      <span className="text-[11px] text-gray-500 md:text-xs">{point.month}</span>
                      <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className="h-2 rounded-full bg-teal-500"
                          style={{ width: `${(point.collected / max) * 100}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-medium tabular-nums text-gray-900 dark:text-white md:text-xs">
                        ${point.collected.toLocaleString()} · {point.efectividad.toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              {saas.monthlySeries.length === 0 && (
                <p className="text-sm text-gray-500">{t('common.noResults')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:gap-5 xl:gap-6">
        <div className="panel-card p-3.5 md:p-4 lg:p-5 xl:p-6">
          <h2 className="mb-3 text-base font-bold text-slate-900 dark:text-white md:mb-4 md:text-lg lg:text-xl">
            {t('pages.dashboard.tenantsSummary')}
          </h2>
          <div className="space-y-3 md:space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('pages.dashboard.activeTenants')}
              </span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-20 rounded-full bg-gray-200 dark:bg-gray-700 md:w-28 lg:w-32">
                  <div
                    className="h-2 rounded-full bg-green-500"
                    style={{ width: `${activeTenantPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                  {stats.activeTenants}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('pages.dashboard.inactiveTenants')}
              </span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-20 rounded-full bg-gray-200 dark:bg-gray-700 md:w-28 lg:w-32">
                  <div
                    className="h-2 rounded-full bg-red-500"
                    style={{ width: `${inactiveTenantPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                  {stats.inactiveTenants}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="panel-card p-3.5 md:p-4 lg:p-5 xl:p-6">
          <h2 className="mb-3 text-base font-bold text-slate-900 dark:text-white md:mb-4 md:text-lg lg:text-xl">
            {t('pages.dashboard.revenueSummary')}
          </h2>
          <div className="space-y-3 md:space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('pages.dashboard.collected')}
              </span>
              <span className="text-base font-semibold tabular-nums text-green-600 dark:text-green-400 md:text-lg">
                ${stats.totalRevenue.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('pages.dashboard.pending')}
              </span>
              <span className="text-base font-semibold tabular-nums text-yellow-600 dark:text-yellow-400 md:text-lg">
                ${stats.pendingRevenue.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-3 dark:border-gray-700 md:pt-4">
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold text-gray-900 dark:text-white">
                  {t('pages.dashboard.total')}
                </span>
                <span className="text-lg font-bold tabular-nums text-teal-600 dark:text-teal-400 md:text-xl">
                  ${(stats.totalRevenue + stats.pendingRevenue).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
